// Copyright 2023, Common Good Learning Tools LLC
import { mapState, mapGetters } from 'vuex'

export default {
	data() { return {
	}},
	computed: {
		...mapState(['site_config', 'my_activities']),
		...mapGetters(['signed_in', 'lti_chooser']),
		// find the ancestor CollectionUnit component where this lesson was launched from (might be null)
		collection_unit_component() { return U.find_ancestor_vue_component(this, 'CollectionUnit') },
		allow_create_activity_from_resource() {
			// we currently allow creating an activity from a resource in the lesson if we're signed in and embedded in a unit that has the assignments tab
			if (!this.signed_in) return false
			if (!this.collection_unit_component) return false
			if (!this.collection_unit_component.show_assignments_tab) return false
			return true
		},

		group_showing: {
			get() { return this.$store.state.lst.lpc_group_showing },
			set(val) { this.$store.commit('lst_set', ['lpc_group_showing', val]) }
		},

		// determine if the component is empty -- i.e. shouldn't be shown in LessonView
		component_empty() { return (component) => {
			// if (component.lc_uuid == 'overview') console.warn('comp empty', component)
			if (!empty(component.lc_content)) return false
			if (component.lc_asset_ids && component.lc_asset_ids.length > 0) return false
			if (component.lc_uuid == 'materials' && this.materials_asset_list.length > 0) return false

			// for the overview component, if we're in the viewer we have to check overview_content
			if (component.lc_uuid == 'overview' && empty(this.original_lesson)) {
				if (!empty(this.overview_content)) return false
			}

			return true
		}},

		resource_option_overrides() { return (item) => {
			let o = {
				// don't allow duplicating from here
				duplicate: false,
			}

			// set create_sparkl_from_resource appropriately
			o.create_sparkl_from_resource = (this.allow_create_activity_from_resource && item.activity_type != 'lesson' && item.type != 'sparkl_bank')

			// you can remove resources if we're in the context of the editor, but not if we're in the context of the viewer
			if (!empty(this.original_lesson)) {
				// note that we used to not let the sparkl that is explicitly tied to the lesson be removed, but now we do
				o.remove = true

			} else o.remove = false

			// for linked lessons, don't allow editing or copying for your use
			if (item.activity_type == 'lesson') {
				o.edit = false
				// we'll deal with this later if someone asks about it
				o.copy_item_for_my_use = false
			}

			// console.warn('resource_option_overrides: '+ JSON.stringify(o))
			return o
		}},

		first_primary_standard() {
			let standards = this.edited_lesson ? this.edited_lesson.standards : this.lesson.standards
			if (empty(standards) || standards.length == 0) return null
			return standards.find(x=>!x.is_secondary)
		},

		standard_supplemental_information() {
			// return supplemental info for the first primary standard, if there is one and if that first standard has supp info
			let standard = this.first_primary_standard
			if (!standard) return ''

			// found a primary standard...
			
			// supplemental information for standards is in CFItem.extensions, which we don't initially load
			// so if we've already loaded it, return it
			if (!empty(standard.extensions)) {
				// console.warn(`SHOWING EXTENSIONS!: ${standard.humanCodingScheme} / ${standard.identifier}`, standard.extensions)
				return this.marked_exemplar(standard.extensions?.supplementalNotes || '')

			// otherwise start loading it, and the computed should be automatically re-called by vue once it's loaded
			} else if (standard.extensions_status == 'unloaded') {
				// console.warn(`LOADING EXTENSIONS: ${standard.humanCodingScheme} / ${standard.identifier}`)
				standard.load_extensions()
				
			} else {
				// console.warn(`STILL LOADING EXTENSIONS?: ${standard.humanCodingScheme} / ${standard.identifier} / ${standard.extensions_status}`)
			}
			return ''
		},

		asset_list() {
			let lesson = this.edited_lesson || this.lesson

			let o = {}
			for (let component of lesson.lesson_plan) {
				let arr = []

				/////////////////////////////////////////////
				// algorithm for components other than materials
				if (component.lc_uuid != 'materials') {
					// ordered list is in lc_asset_ids; pull from lessons or resources
					for (let asset_id of component.lc_asset_ids) {
						let asset = lesson.resources.find(x=>x.resource_id == asset_id)
						if (empty(asset)) asset = lesson.lessons.find(x=>x.lesson_id == asset_id)
						if (empty(asset)) {
							console.warn('asset not found: ' + asset_id)
							continue
						}
						arr.push(asset)
					}

				/////////////////////////////////////////////
				// algorithm for materials component
				} else {
					// if this lesson has case_resource_extensions...
					/* here's what lesson_plan_case_extension_resource_schema might look like in site_config:
						'lesson_plan_case_extension_resource_schema' => [
							'tfgURL' => [ 'description' => 'Teacher Facilitation Guide', 'type' => 'website' ],
							'slideURL_EN' => [ 'description' => 'Lesson Slides (EN)', 'type' => 'website', 'teacher_facing' => false ],
						],
					*/
					if (!empty(lesson.case_resource_extensions)) {
						// if site_config defines a case_extension_resource_schema, add any standardized resources specified there
						let case_extension_resource_schema = this.site_config.lesson_plan_case_extension_resource_schema
						if (!empty(case_extension_resource_schema)) {
							for (let key in case_extension_resource_schema) {
								let url = lesson.case_resource_extensions[key]

								// if a resource with this url already exists in lesson.resources, don't add it here
								if (lesson.resources.find(x=>x.url == url)) continue

								if (!empty(url)) {
									let o = case_extension_resource_schema[key]
									let r = new Resource({
										resource_id: `TEMP-${U.new_uuid()}`,
										url,
										type: o.type ?? 'website',
										description: o.description ?? key,
										teacher_facing: o.teacher_facing ?? true,
									})
									arr.push(r)
								}
							}
						}

						// and if we have case_resource_extensions.lessonResources array, add them too
						if (!empty(lesson.case_resource_extensions.lessonResources)) {
							for (let ro of case_resource_extensions.lessonResources) {
								// each of these objects *must* have a `url`, and can optionally have a `description`, `type`, and `teacher_facing` value
								
								// if a resource with this url already exists in lesson.resources, don't add it here
								if (lesson.resources.find(x=>x.url == ro.url)) continue

								let r = new Resource({
									resource_id: `TEMP-${U.new_uuid()}`,
									url: ro.url,
									type: ro.type ?? 'website',
									description: ro.description ?? ro.url,
									teacher_facing: ro.teacher_facing ?? true,
								})
								arr.push(r)
							}
						}
					}

					// now add "true" resources and lessons
					arr = [...arr, ...lesson.resources, ...lesson.lessons]

					// add all anchor links in the lc_content of components as "TEMP" resources, so that they show up in the resource list at the bottom
					for (let group of this.lp_component_scheme.groups) {
						for (let lc_uuid of group.group_components) {
							let component = lesson.lesson_plan.find(x=>x.lc_uuid == lc_uuid) 
							if (empty(component?.lc_content)) continue	// shouldn't happen, but just in case

							// use U.merge_duplicate_links to consolidate duplicate links (seen in CPS Foreign Language lessons)
							let html = U.merge_duplicate_links(component.lc_content)

							let re = /<a\s+(?:[^>]*?\s+)?(href|onclick)=(["'])(.*?)\2[^>]*?>(.*?)<\/a>/gi
							let matches = html.matchAll(re)
							for (let match of matches) {
								let scheme = match[1]
								let url = match[3]
								let description = match[4]

								// "formal" resource links will have onclick handlers
								if (scheme == 'onclick') {
									// TODO: deal with linked lessons
									// see format for this link in resources.js froala_resource_link_html
									let jq = $(match[0])
									let resource_id = jq.attr('data-resource-id')
									let type = jq.attr('data-resource-type')
									let teacher_facing = jq.attr('data-teacher-facing') == 'true'
								
									// if a resource with this resource_id already exists in lesson.resources, don't add it here
									if (lesson.resources.find(x=>x.resource_id == resource_id)) continue

									let r = new Resource({
										resource_id: resource_id,
										type,
										description,
										teacher_facing
									})
									r.is_lesson_temp_resource = true
									arr.push(r)
									// note: the full resource data will be retrieved if/when the user clicks on the resource, in ResourceCollectionItem

								// else it's a "simple" link
								} else {
									// if a resource with this url already exists in lesson.resources, don't add it here
									if (lesson.resources.find(x=>x.url == url)) continue

									let description_text = U.html_to_text(description)

									// if description is empty or if description_text doesn't start with a capital letter or pictogram, use the url as the description
									// otherwise assume `description` is the title for the resource
									if (empty(description) || !(/^([A-Z]||\p{Extended_Pictographic})/.test(description_text))) {
										description_text = description = url
									}

									// limit to max 60 chars
									if (description_text.length > 60) {
										description = description_text.substr(0, 60)
										if (description != description_text) description += '…'
									}

									// remove all tags from the description text *except* italics
									description = description.replace(/<(?!\/?(i|em)\b)[^>]+>/gi, '')
									// note that this will also remove <span> tags that might be introducing attributes such as color:xxx

									// console.warn(`${lc_uuid} description: “${description}”`)

									let r = new Resource({
										resource_id: `TEMP-${U.new_uuid()}`,
										type: 'website',
										url,
										description,
										teacher_facing: true,
									})

									let index = arr.findIndex(x=>x.url == r.url)
									if (index > -1) {
										// we don't want to add twice. if the two descriptions are identical, just skip the current one
										if (arr[index].description == r.description) {
											continue
										}

										// else try to choose the version with the better description
										const LINK_RE = /http(s)?:\/\/\w.*\./
										let a2 = [r, arr[index]].sort((a,b) => {
											// prefer non-http description over http description
											if (a.description.search(LINK_RE) != 0 && b.description.search(LINK_RE) == 0) return -1
											if (b.description.search(LINK_RE) != 0 && a.description.search(LINK_RE) == 0) return 1

											// prefer longer description (code above ensures we won't go above 60)
											return a.description.length - b.description.length
										})
										
										// if we prefer the new one, splice it in place of the old one; then continue
										if (a2[0] == r) {
											arr.splice(index, 1, r)
										}
										continue
									}

									// note that we don't set is_lesson_temp_resource here, because there's no cureum resource object to look up (or if there is one, we don't know the resource_id)
									arr.push(r)
								}
							}
						}
					}
				}

				o[component.lc_uuid] = arr
			}

			return o
		},
		materials_asset_list() { return this.asset_list['materials'] },
	},
	watch: {
	},
	methods: {
		// master data lesson plan hash: convenience object for getting properties of the master data for each component
		// this is used in both LessonView and LessonEditorEnhanced; in both cases lesson_master and lp_component_scheme are defined in the component
		construct_mdlph() {
			if (empty(this.lp_component_scheme)) return {}
			// master data lesson plan hash: convenience object for getting properties of the master data for each component
			// merging in data from the currently-selected lp_component_scheme group
			let o = {}
			for (let c of this.lesson_master.lesson_plan) {
				c = object_copy(c)

				// if no lc_title_plural, set to lc_title
				if (empty(c.lc_title_plural)) c.lc_title_plural = c.lc_title

				// get lp_scheme_group
				let lp_scheme_group = this.lp_component_scheme?.groups.find(x=>x.group_components.includes(c.lc_uuid))
				c.lp_scheme_group = lp_scheme_group

				// merge in color if scheme provides it
				if (!empty(lp_scheme_group)) c.lc_color = lp_scheme_group.color

				// if scheme includes alt_component_data for this component, merge it in
				// note that acd can be defined as either an array or an object
				let acd = this.lp_component_scheme?.alt_component_data
				if (acd) {
					let acdc = $.isArray(acd) ? acd.find(x=>x.lc_uuid == c.lc_uuid) : acd[c.lc_uuid]
					if (acdc) {
						for (let key in acdc) {
							c[key] = acdc[key]
						}
					}
				}

				o[c.lc_uuid] = c
			}

			return o
		},
		
		section_text(component) {
			let content = component.lc_content

			// for the overview component, use overview_content in the lesson viewer (see the computed for overview_content)
			if (empty(this.original_lesson)) {
				if (component.lc_uuid == 'overview') {
					content = this.overview_content
				}
			}

			if (empty(content)) return ''
	
			// render latex
			content = U.render_latex(content)

			// make sure all a tags open in a new window (an a tag might get two target tags, but that's OK)
			content = content.replace(/<a /g, '<a target="_blank" ')

			// add copy icons after <hr> tags
			let arr = content.split(/<hr>/)
			let index = 0
			content = content.replace(/(<hr>)/g, ($0, $1) => {
				if (empty(arr[index+1])) return $1

				let s = `${$1}<div class="k-section-text-copy-btn" data-lc_uuid="${component.lc_uuid}" data-section-part-index="${index}"><i class="fas fa-copy"></i></div>`
				++index
				return s
			})

			// now, if the content *starts* with an <hr>, take it out
			content = content.replace(/^<hr>/, '')

			let mdc = this.master_component(component)
			if (empty(mdc)) return content

			// regardless of lc_text_format, if the content includes a block tag, just return the plain content
			if (content.search(/<(p|ul|li|div|table)\b/) > -1) {
				return content
			}

			// for single-line style (e.g. questions), wrap in a paragraph; else just return the plain content
			// (we used to do more complex stuff here, but now we handle inserting formatting in LessonEditorEnhanced, and we convert old (pre-8/13/2025) content in lessons.js)
			if (mdc.lc_text_format == 'line') {
				return `<p>${content}</p>`
			} else {
				return content
			}
		},

		toggle_component_group_showing($event, component, val, scroll_to_group = false) {
			let uuid = component.lc_uuid
			// do things this way to ensure reactivity of the group_showing object
			let gs = object_copy(this.group_showing)
			if (empty(val)) val = !gs[uuid]

			// if cmd/alt is held down while clicking, toggle all groups
			if ($event && U.meta_or_alt_key($event)) {
				for (let index in gs) {
					gs[index] = val
				}
			} else {
				gs[uuid] = val

				// if we're toggling a single group on, scroll to the group, unless explicitly told not to
				if (val && scroll_to_group === true) {
					this.scroll_to_stage(uuid)
				}
			}
			this.group_showing = gs
		},

		find_group_for_component(component) {
			let blocks = this.right_blocks || this.left_blocks
			let i = blocks.findIndex(x=>x.lc_uuid == component.lc_uuid)
			for (i = i - 1; i >= 0; --i) {
				let c = blocks[i]
				if (c.lc_uuid.indexOf('group_') == 0) {
					return c
				}
			}
			return null	// shouldn't happen
		},

		component_group_showing(component) {
			if (typeof(component) == 'string') return true
			let gc = this.find_group_for_component(component)
			if (empty(gc)) {
				// console.warn('no group!', component.lc_uuid)
				return
			}
			return this.group_showing[gc.lc_uuid] === true
		},

		show_group_for_component(component) {
			// if component is a string, it's 'standards' (or something similar) that doesn't have a group
			if (typeof(component) == 'string') return

			let gc = this.find_group_for_component(component)
			this.toggle_component_group_showing(null, gc, true, true)
		},

		show_at_least_one_group() {
			if (empty(this.lesson_master) || empty(this.lesson?.lp_component_scheme)) {
				setTimeout(()=>this.show_at_least_one_group(), 100)
				return
			}

			let gs = object_copy(this.group_showing)
			let at_least_one_open = false

			for (let group of this.lp_component_scheme.groups) {
				if (gs[group.lc_uuid] === true) {
					// at least one is already open, so return
					return
				}
			}
			
			// if we get to here, open the first group
			this.toggle_component_group_showing(null, this.lp_component_scheme.groups[0], true)
		},

		component_stumpy_toggled(lc_uuid, val) {
			// if (val == true) this.go_to_stage(lc_uuid)
			// else this.last_clicked_component = null
			let lesson = this.lesson || this.edited_lesson
			let component = lesson.lesson_plan.find(x=>x.lc_uuid == lc_uuid) 
			if (empty(component?.lc_content)) return	// shouldn't happen, but just in case
			
			if (val == true) {
				this.last_clicked_component = component
				this.scroll_to_stage(component)
			} else {
				this.last_clicked_component = null
			}
		},

		scroll_to_stage(component) {
			// scroll to stage; note that target selector logic handles whether component is an actual component or a stage name (e.g. 'title')
			setTimeout(x=>{
				let target = $(this.$el).find(`.k-lpe-alba-content-section-${component?.lc_uuid ?? component}`)[0]
				if (!empty(target)) {
					let container = $(this.$el).find('.k-lpe-alba-content-inner')[0]
					// let container = $('.v-dialog').find('.v-card__text')[0]
					this.$vuetify.goTo(target, {container:container, offset:0})
				}
			}, 100)
		},

		activate_section_text_copy_btns() {
			setTimeout(x=>{
				$(this.$el).find('.k-section-text-copy-btn').on('click', (evt) => {
					let lc_uuid = evt.currentTarget.getAttribute('data-lc_uuid')
					let part_index = evt.currentTarget.getAttribute('data-section-part-index')*1

					let lesson = this.lesson || this.edited_lesson

					let lpc = lesson.lesson_plan.find(x=>x.lc_uuid == lc_uuid)
					let arr = lpc.lc_content.split(/<hr>/)
					let s = arr[part_index+1]
					// if (this.mdlph) {
					// 	let icon = this.mdlph[lc_uuid].lc_icon
					// 	s = `<div class="float-left mr-1"><i class="${icon}">x</i></div>${s}`
					// }
					console.warn(s)
					U.copy_to_clipboard(s, true)	// second param makes this copy so that we paste formatted text
					// TODO flash copy btn
					this.$inform('Copied!')
				})
			}, 1000)
		},

		get_resource_collection_item(resource) {
			// this has to work for lessons or resources...
			// also make it work whether we get a resource_id/lesson_id or the full resource/lesson
			let resource_id, type
			if (typeof(resource) == 'object') {
				if (!empty(resource.lesson_id)) {
					type = 'lesson'
					resource_id = resource.lesson_id
				} else {
					type = 'resource'
					resource_id = resource.resource_id
				}
			
			} else {
				// lesson id's are integers
				type = (!isNaN(resource * 1)) ? 'lesson' : 'resource'
				resource_id = resource
			}

			if (type == 'resource') {
				let rci = this.$refs.resource_collection_item.find(x=>x.item.resource_id==resource_id)
				// let rci = this.$refs.resource_collection_item.find(x=>x.item.resource_id==resource_id&&$(x.$el).is(':visible'))
				if (!rci) console.error('couldn’t get rci for ', resource)
				return rci
			} else {
				let rci = this.$refs.resource_collection_item_linked_lesson.find(x=>x.item.resource_id==resource_id)
				// let rci = this.$refs.resource_collection_item_linked_lesson.find(x=>x.item.resource_id==resource_id&&$(x.$el).is(':visible'))
				if (!rci) console.error('couldn’t get rci for ', resource)
				return rci
			}
		},

		// this is called when the user clicks on a resource in a component
		show_resource(resource, resource_link_id, flag) {
			let rci = this.get_resource_collection_item(resource)
			if (rci) {
				if (this.lti_chooser) {
					rci.lti_chooser_preview()
				} else {
					// send flag='from_quick_look' to open items that don't *have* to show up in the quick-look dialog (i.e. sparkl activities) directly
					rci.card_clicked(flag)
				}
			}

			// highlight the clicked link object?? (PW 8/16/2026: I'm not sure what this does in practice)
			$(this.$el).find('[data-resource-link-id]').removeClass('k-last-lesson-quick-look-resource')
			if (!empty(resource_link_id)) {
				$(this.$el).find(`[data-resource-link-id=${resource_link_id}]`).addClass('k-last-lesson-quick-look-resource')
			}
		},

		// this is called when the user clicks to add to gc from a component
		add_to_gc(resource, resource_link_id) {
			let rci = this.get_resource_collection_item(resource)
			if (rci) {
				rci.add_to_gc()
			}
		},

		// this is called when the user clicks the lti_chooser "choose" btn from a component
		lti_chooser_choose(resource) {
			let rci = this.get_resource_collection_item(resource)
			if (rci) {
				rci.lti_chooser_choose()
			}
		},

		linked_lesson_opened() {
			// code in App.vue depends on having a reference to the currently-open lesson_view_component, so if we open a linked lesson,
			// save the current value of vapp.lesson_view_component so we can restore it when we close the new lesson viewer
			this.previous_lesson_view_component = vapp.lesson_view_component
		},

		linked_lesson_closed() {
			vapp.lesson_view_component = this.previous_lesson_view_component
		},

		// find and open the lesson sparkl activity
		open_lesson_sparkl_activity() {
			let lesson = this.edited_lesson || this.lesson	// so this works from LessonView or LessonEditorEnhanced
			let r = lesson.resources.find(x=>x.type == 'sparkl' && x.url == lesson.sparkl_activity_id)

			if (!r) {
				this.$alert(`The ${this.site_config.sparkl_app_name} Activity for this lesson appears to have been removed from the lesson resources.`)
				return
			}
			
			this.get_resource_collection_item(r)?.show_sparkl('edit')
		},

		create_activity_from_resource(resource, flag) {
			if (this.$store.getters.simulating_user) { this.$alert('You are not allowed to do this while simulating another user.'); return; }

			// if the resource is already sparkl activity, we just want to assign it...
			if (resource.type == 'sparkl') {
				this.assign_sparkl_activity(resource)
				return
			}
			
			this.$prompt({
				title: 'Use Resource in Student Activity',
				text: `To use this ${resource.type_label(true)} in a Student Activity, start by entering a student-facing description for the linked resource below, then click <nobr><b class="deep-orange--text text--darken-4">ADD TO EXISTING ACTIVITY</b></nobr> or <nobr><b class="blue--text text--darken-4">CREATE NEW ACTIVITY</b>.`,
				initialValue: resource.description,
				disableForEmptyValue: true,

				extraBtnText: 'Add to Existing Activity',
				extraBtnIconAfter: 'fas fa-circle-arrow-right',
				extraBtnColor: 'deep-orange darken-4',

				acceptText: 'Create New Activity',
				acceptIconAfter: 'fas fa-circle-arrow-right',
				acceptColor: 'blue darken-4',

				dialogMaxWidth: 700,
			}).then((rv) => {
				// if rv is a string, it means they clicked to add new
				if (typeof(rv) == 'string') {
					rv = rv.trim()
					if (empty(rv)) return

					// proceed to create a new activity from the resource
					this.complete_create_activity_from_resource(resource, rv, null)

				// otherwise it means that they clicked to add to existing
				} else {
					rv = rv[0].trim()
					if (empty(rv)) return

					// proceed to copy the data for a new sparkl exercise part to the clipboard
					this.copy_sparkl_part_to_clipboard(resource, rv)
				}
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		copy_sparkl_part_to_clipboard(resource, link_description) {
			this.$confirm({
				title: 'Add Resource to Existing Activity',
				text: `Click the button below to copy to your clipboard a ${this.site_config.sparkl_app_name} exercise part linking to this ${resource.type_label(true)}. You can then open any activity and paste the resource into the activity, by either:<ul><li>Clicking ADD ANOTHER EXERCISE, then choosing “Import questions or exercises” > “<b>Paste</b> an exercise from your clipboard” or</li><li>Clicking the “kebab” (<i class="fas fa-ellipsis-v"></i>) menu for an existing exercise and choosing “Paste exercise part from clipboard”</li></ul>`,
				dialogMaxWidth: 540,
				acceptText: 'Copy Exercise Part to Clipboard',
			}).then(() => {
				// copy to the clipboard the code to paste into an activity
				let s = resource.get_sparkl_activity_html(link_description, 'exercise_part')
				U.copy_to_clipboard(s)
				this.inform(`${this.site_config.sparkl_app_name} exercise part copied to clipboard.`)
			}).catch(n=>{console.log(n)}).finally(f=>{})
		},

		complete_create_activity_from_resource(resource, link_description, existing_activity_id) {
			// console.warn('complete_create_activity_from_resource', resource, link_description, existing_activity_id)
			if (resource.type == 'sparkl') {
				this.$alert({
					title: 'Assign Activity',
					text: `The ${this.site_config.sparkl_app_name} editor has been opened for your activity. You can now enhance the activity if you wish (e.g. by adding instructions, questions, and/or additional content), then click the CLOSE ACTIVITY button at the top of the window, and assign the activity to your students.`
				})
			} else {
				this.$alert({
					title: 'Create New Activity',
					text: `The ${this.site_config.sparkl_app_name} activity editor has been opened, with this ${resource.type_label(true)} added to the activity. You can now enhance the activity if you wish (e.g. by adding instructions, questions, and/or additional content), then click the CLOSE ACTIVITY button at the top of the window, and assign the activity to your students.`
				})
			}

			if (this.collection_unit_component) {
				// note that the 'type' here is 'activity' (*not* 'sparkl')
				this.collection_unit_component.create_activity_from_resource({type:'activity', starting_resource:resource, link_description: link_description, existing_activity_id: existing_activity_id})
			} else this.$alert('Error 38739 in LessonEnhancedMixin')	// shouldn't happen
			// TODO: could allow for adding to sandbox

			// if we're in the lesson editor...
			if (this.$options.name == 'LessonEditor') {
				// first save and close, 
				this.save_and_close()
				// then find the lesson view if it's showing and emit to close
				let lesson_component = U.find_ancestor_vue_component(this, 'DirectivesListItem')
				if (lesson_component) {
					setTimeout(x=>lesson_component.$emit('close_lesson'), 80)
				}
			} else {
				// else we just have to close this component
				this.$emit('close_lesson')
			}
		},

		assign_sparkl_activity(resource) {
			// first see if the activity is already assigned
			if (this.my_activities.find(x=>x.resource_id == resource.resource_id)) {
				this.$alert('You have already assigned this Sparkl activity to your students.')
				// TODO: offer to create a different assignment??
				return
			}
			this.complete_create_activity_from_resource(resource, '', resource.url)
		},

		copy_to_shadow_unit_finish(copied_item) {
			// console.warn('copy_to_shadow_unit_finish in LessonEnhancedMixin')
			// this is called via an emitter from ResourceCollectionItem when a Sparkl activity attached an agency-sanctioned lesson is copied for the user's use.

			// we should only be here for sparkl activities...
			if (copied_item.type != 'sparkl') {
				this.$alert('We should not be here!!')
				return
			}

			// make sure we have access to the default collection (we should)
			let dc = this.$store.getters.my_default_collection
			if (empty(dc?.units[1]?.resources?.length)) {
				this.$alert('Error: can’t save activity resource (323)')
				return
			}

			let msg = 'A copy of the activity has been created for your use. You can access the copied activity from this Lesson, as well as from your Content Sandbox.<br><br>You can use the activity with your students, and/or modify the activity if you wish, now.'

			if (this.site_config.copied_for_my_use_warning) {
				msg += `<div class="mt-3 pt-3 px-5" style="border-top:1px solid #ccc; font-size:14px; line-height:18px;">${this.site_config.copied_for_my_use_warning}</div>`
			}

			// add the copied sparkl resource to the user's sandbox -- push to default_collection, unit 1.resources, then call the save_default_collection_resources service (see CollectionUnit)
			dc.units[1].resources.push(copied_item)
			this.$store.dispatch('save_default_collection_resources').then(() => {
				console.warn('save_default_collection_resources done')

				// then show the message, after a pause to allow the new Sparkl to be loaded
				setTimeout(x=>{
					this.$alert({
						text: msg,
						dialogMaxWidth: 500,
					}).then(y => {
					})
				}, 250)
			})
		},

		// this is a simplified version of the fn from Satchel that renders supplemental notes
		marked_exemplar(exs) {
			if (!exs) return ''
			// note that examplars can either use html or markdown; use marked if we don't have any html tags
			// also note that if the author uses latex, they shouldn't also try to use markdown, because the latex renderer will render the latex as html, so marked() won't be run here

			if (exs.search(/<\w+[^<]*>/) == -1) exs = U.marked_latex(exs)
			else exs = U.render_latex(exs)

			// if exemplar includes <hr>s, convert the line after each <hr> to an expandable div
			let arr = exs.split('<hr>')
			if (arr.length == 1) {
				// no blocks; just return exs
				return exs

			} else {
				// start with whatever was in arr[0] (which might be an empty string)
				let html = arr[0].replace(/(<br>)+$/, '')

				// for each additional block
				for (let i = 1; i < arr.length; ++i) {
					// try to extract the first line, which is a little tricky because it might be a paragraph, or it might be a blank line separated by a <br> or a <ol> or <ul>
					let header, footer
					if (arr[i].search(/^\s*<p>(.*?)<\/p>([\s\S]*)/) > -1) {
						header = RegExp.$1
						footer = RegExp.$2
					} else if (arr[i].search(/^(.*?)((<br|<ol|<ul)[\s\S]*)/) > -1) {
						header = RegExp.$1
						footer = RegExp.$2
					}

					if (header && footer) {
						// remove leading <br> from footer if there (that br would be the end of the header line)
						footer = footer.replace(/^<br>/, '')

						// TODO: remember if these have been opened by the user
						let block_id = U.new_uuid()
						html += `<div class="k-exemplar-block-header" data-header-id="${block_id}" data-block-showing="no"><i class="fas fa-caret-right"></i> ${header}</div><div class="k-exemplar-block-body" style="display:none" data-block-id="${block_id}">${footer}</div>`
						// html += `<div class="k-exemplar-block-header" data-header-id="${block_id}" data-block-showing="yes"><i class="fas fa-caret-right"></i> ${header}</div><div class="k-exemplar-block-body" data-block-id="${block_id}">${footer}</div>`

						this.$nextTick(x=>{
							$('[data-header-id=' + block_id + ']').click(function() {
								let jq = $(this)
								let block_id = jq.attr('data-header-id')
								let showing = jq.attr('data-block-showing')
								if (showing == 'no') {
									$('[data-block-id=' + block_id + ']').slideDown(200)
									jq.find('.fas').removeClass('fa-caret-right').addClass('fa-caret-down')
									jq.attr('data-block-showing', 'yes')
								} else {
									$('[data-block-id=' + block_id + ']').slideUp(100)
									jq.find('.fas').removeClass('fa-caret-down').addClass('fa-caret-right')
									jq.attr('data-block-showing', 'no')
								}
							})
						})

					} else {
						html += sr('<div class="k-exemplar-block-body-no-header">$1</div>', arr[i])
					}
				}

				return html
			}
		},

		standards_component_left_bind() {
			let o = {}
			if (this.site_config.lpc_muted_colors === 'yes') {
				o.class = `k-lpe-alba-menu-stage`
			} else {
				//  class="k-lpe-alba-menu-stage amber lighten-4"
				o.class = `k-lpe-alba-menu-stage ${this.standards_component.lc_color} lighten-4`
			}
			return o
		},

		standards_component_right_bind() {
			let o = {}
			if (this.site_config.lpc_muted_colors === 'yes') {
				o.class = `k-lpe-alba-content-section k-lpe-alba-content-section-standards`
			} else {
				o.class = `k-lpe-alba-content-section k-lpe-alba-content-section-standards ${this.standards_component.lc_color} lighten-4`
			}
			return o
		},

		left_block_component_bind(component) {
			let o = {}
			// if this is a group
			if (component.title) {
				// lpc_muted_colors -- for CPS
				if (this.site_config.lpc_muted_colors === 'yes') {
					o.class = `${component.color} white--text`
				} else {
					o.class = `${component.color} lighten-3 black--text`
				}

			// not a group
			} else {
				// lpc_muted_colors -- for CPS
				if (this.site_config.lpc_muted_colors === 'yes') {
					// o.class = this.mdlph[component.lc_uuid].lc_color.replace(/ darken/, '--text text--darken')
				} else {
					o.class = `${this.mdlph[component.lc_uuid].lc_color} lighten-5`
				}
			}
			return o
		},

		right_block_component_bind(component) {
			let o = {}
			// if this is a group
			if (component.title) {
				// lpc_muted_colors -- for CPS
				if (this.site_config.lpc_muted_colors === 'yes') {
					o.class = `${component.color} k-lpe-alba-content-section-${component.lc_uuid} white--text`
				} else {
					o.class = `${component.color} lighten-3 k-lpe-alba-content-section-${component.lc_uuid}`
				}

			// not a group
			} else {
				// lpc_muted_colors -- for CPS
				if (this.site_config.lpc_muted_colors === 'yes') {
					// blue darken-3
					// o.class = this.mdlph[component.lc_uuid].lc_color.replace(/ darken/, '--text text--darken')
				} else {
					o.class = `${this.mdlph[component.lc_uuid].lc_color}--text text--darken-4`
				}
			}
			return o
		},

	}
}